import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { getErrorsFromConditions, transformConditionsFromBackend } from '@/common/components/ConditionsEditor/helpers'
import { languages } from '@/common/constants'
import { defaultBlocks } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/ContentBlocks/constants'
import { editorModes } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/EditorModeSelector/constants'
import { booleanFields } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/EmailTemplateConditionsEditor/constants'
import useLiquidErrors from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/hooks/useLiquidErrors/useLiquidErrors'
import { validateTriggers } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Trigger/helpers'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import routes from '@/Router/routes.constants'

import {
  areTriggersValid,
  generateTriggerId,
  getEditTemplatePayload,
  getLiveConditionErrors,
  hasContentInAllAvailableLanguages,
  hasLiquidContentInAllAvailableLanguages,
  hasLiquidErrorsInAvailableLanguages,
  hasSubjectInAllAvailableLanguages,
  transformContent,
  transformPreheader,
  transformSubject,
} from './helpers/helpers'
import { servicesByConfigurationMode } from './constants'

const useEmailTemplateDetail = ({
  storeSupportedLanguages,
  templateId,
  navigate,
  setToastInfo,
  configurationMode,
  merchantId,
  isUnifiedDashboard,
  returnPortalId,
  storeCurrencyCode,
}) => {
  const { t } = useTranslation()
  const isNewTemplate = templateId === 'new'
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })
  const availableLanguages = languages(t)?.filter(lang => storeSupportedLanguages?.includes(lang.value))
  const [languageSelected, setLanguageSelected] = useState(availableLanguages?.[0]?.value)
  const [subject, setSubject] = useState({})
  const [preheader, setPreheader] = useState({})
  const [mode, setMode] = useState(editorModes.html)
  const [content, setContent] = useState({})
  const [liquidContent, setLiquidContent] = useState({})
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [blocks, setBlocks] = useState(defaultBlocks)
  const [triggers, setTriggers] = useState([{ id: generateTriggerId(), type: '', values: [] }])
  const [conditions, setConditions] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState([])
  const { liquidErrors, validateLiquidContent } = useLiquidErrors({
    liquidContent,
    enabled: mode === editorModes.liquid,
  })
  const {
    data,
    fetchData: fetchEmailTemplate,
    loading,
  } = useFetch(services.getDetailEmailTemplate, {
    params: { ...entityId, templateId },
    doInitialRequest: false,
  })
  const { loading: editingDetailEmailTemplate, fetchData: editDetailEmailTemplate } = useFetch(
    services.editDetailEmailTemplate,
    {
      doInitialRequest: false,
    }
  )
  const { loading: creatingEmailTemplate, fetchData: createEmailTemplate } = useFetch(services.createEmailTemplate, {
    doInitialRequest: false,
  })

  useEffect(() => {
    if (!isNewTemplate && configurationMode && !data && !loading) {
      fetchEmailTemplate({ ...entityId, templateId })
    }
  }, [configurationMode, data, entityId, fetchEmailTemplate, isNewTemplate, loading, templateId])

  useEffect(() => {
    if (data) {
      setSubject(transformSubject(data))
      setContent(transformContent(data))

      setPreheader(transformPreheader(data))

      if (data.mode) {
        setMode(data.mode)
      }

      if (data.liquid_content) {
        setLiquidContent(data.liquid_content)
      }

      if (data.name) {
        setName(data.name)
      }

      if (data.description) {
        setDescription(data.description)
      }

      if (data.blocks) {
        setBlocks(data.blocks)
      }

      if (data.triggers) {
        setTriggers(data.triggers.map(trigger => ({ ...trigger, id: generateTriggerId() })))
      }

      if (data.conditions) {
        setConditions(transformConditionsFromBackend(data.conditions, { booleanFields }))
      }
    }
  }, [data])

  const handleChangeLanguage = newLanguage => {
    setLanguageSelected(newLanguage)
  }

  const handleChangeSubject = data => {
    setHasChanges(true)
    setSubject(subject => ({
      ...subject,
      [languageSelected]: {
        text: data.text,
        html: data.html,
      },
    }))
  }

  const handleChangePreheader = data => {
    setHasChanges(true)
    setPreheader(preheader => ({ ...preheader, [languageSelected]: data.html }))
  }

  const handleChangeContent = data => {
    setHasChanges(true)
    setContent(content => ({
      ...content,
      [languageSelected]: {
        text: data.text,
        html: data.html,
      },
    }))
  }

  const handleChangeLiquidContent = value => {
    setHasChanges(true)
    setLiquidContent(liquidContent => ({
      ...liquidContent,
      [languageSelected]: value,
    }))
  }

  const handleChangeMode = newMode => {
    setHasChanges(true)
    setMode(newMode)
  }

  const handleChangeName = e => {
    setHasChanges(true)
    setName(e.target.value)
  }

  const handleChangeDescription = e => {
    setHasChanges(true)
    setDescription(e.target.value)
  }

  const handleChangeBlocks = newBlocks => {
    setHasChanges(true)
    setBlocks(newBlocks)
  }

  const handleChangeTrigger = (triggerId, changes) => {
    setHasChanges(true)
    setTriggers(prev => prev.map(trigger => (trigger.id === triggerId ? { ...trigger, ...changes } : trigger)))
  }

  const handleAddTrigger = () => {
    setHasChanges(true)
    setTriggers(prev => [...prev, { id: generateTriggerId(), type: '', values: [] }])
  }

  const handleRemoveTrigger = triggerId => {
    setHasChanges(true)
    setTriggers(prev => prev.filter(trigger => trigger.id !== triggerId))
  }

  const handleChangeConditions = ({ conditions: newConditions }) => {
    setHasChanges(true)
    setConditions(newConditions)
  }

  const isLiquidContentSavable = async () => {
    try {
      const currentLiquidErrors = await validateLiquidContent()

      return !hasLiquidErrorsInAvailableLanguages(currentLiquidErrors, availableLanguages)
    } catch {
      setToastInfo({
        show: true,
        message: t('No se ha podido comprobar el código Liquid. Inténtalo de nuevo.'),
        type: 'error',
      })

      return false
    }
  }

  const onSaveChanges = async () => {
    const triggerErrors = validateTriggers(triggers)

    if (triggerErrors.length) {
      setErrors(triggerErrors)
      return
    }

    setErrors([])

    if (mode === editorModes.liquid && !(await isLiquidContentSavable())) return

    const payload = getEditTemplatePayload({
      subject,
      preheader,
      mode,
      content,
      liquidContent,
      name,
      description,
      blocks,
      triggers,
      conditions,
      defaultCurrency: storeCurrencyCode,
    })
    try {
      if (isNewTemplate) {
        const response = await createEmailTemplate({ ...entityId, payload })
        setToastInfo({ show: true, message: t('Plantilla creada correctamente'), type: 'success' })
        const route = isUnifiedDashboard
          ? routes.merchantConfigurationPage.emailTemplateDetail
          : routes.configurationPage.emailTemplateDetail
        navigate(route.replace(':id', response.id), { replace: true })
      } else {
        await editDetailEmailTemplate({ ...entityId, templateId, payload })
        setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
      }
      setHasChanges(false)
    } catch (error) {
      const errorMessage = isNewTemplate ? t('Error al crear la plantilla') : error.errorMessage
      setToastInfo({ show: true, message: errorMessage, type: 'error' })
    }
  }

  const onCancelChanges = () => {
    const route = isUnifiedDashboard
      ? routes.merchantConfigurationPage.emailTemplates
      : routes.configurationPage.emailTemplates
    navigate(route)
  }

  const areConditionsValid = () => getErrorsFromConditions(conditions).length === 0

  const conditionErrors = getLiveConditionErrors(conditions)

  const isContentValid = () => {
    if (mode === editorModes.liquid) {
      return (
        hasLiquidContentInAllAvailableLanguages(liquidContent, availableLanguages) &&
        !hasLiquidErrorsInAvailableLanguages(liquidErrors, availableLanguages)
      )
    }

    return hasContentInAllAvailableLanguages(content, availableLanguages)
  }

  const isFormValid = () => {
    return (
      name.trim() !== '' &&
      description.trim() !== '' &&
      hasSubjectInAllAvailableLanguages(subject, availableLanguages) &&
      isContentValid() &&
      areTriggersValid(triggers) &&
      areConditionsValid()
    )
  }

  return {
    availableLanguages,
    languageSelected,
    handleChangeLanguage,
    subject: subject?.[languageSelected]?.html || '',
    subjectText: subject?.[languageSelected]?.text || '',
    handleChangeSubject,
    preheader: preheader[languageSelected] || '',
    handleChangePreheader,
    mode,
    handleChangeMode,
    content:
      mode === editorModes.liquid ? liquidContent[languageSelected] || '' : content[languageSelected]?.html || '',
    handleChangeContent,
    handleChangeLiquidContent,
    liquidError: liquidErrors[languageSelected],
    name,
    description,
    handleChangeName,
    handleChangeDescription,
    fetchingTemplate: loading,
    onSaveChanges,
    onCancelChanges,
    hasChanges,
    editingDetailEmailTemplate: editingDetailEmailTemplate || creatingEmailTemplate,
    isFormValid,
    isNewTemplate,
    blocks,
    handleChangeBlocks,
    triggers,
    handleChangeTrigger,
    handleAddTrigger,
    handleRemoveTrigger,
    conditions,
    conditionErrors,
    handleChangeConditions,
    errors,
  }
}

export default useEmailTemplateDetail
